import {
  ConditionName,
  CustomTheme,
  getOptInByLoanIdAndBorrowerId,
  OnboardingDetailsRole,
  OptInBorrower,
  OptInCondition,
  postSendBorrowerOptInEmail,
} from '@nemuru-eng/js-api-client';
import { useMemo, useRef } from 'react';

import { LoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/entities/PaymentMethodDetail/LoanPaymentMethodDetailEntity';
import { useCustomTheme } from '@/hooks/useCustomTheme';

export enum ConsentType {
  CAIXABANK = 'caixabank',
  NEMURU = 'nemuru',
  TECHNOLOGY_PROVIDER = 'technology_provider',
}

const ConditionNameToConsentType: Partial<Record<ConditionName, ConsentType>> = {
  [ConditionName.CAIXABANK_MERCHANT_DATA]: ConsentType.CAIXABANK,
  [ConditionName.NEMURU_TERMS_AND_CONDITIONS]: ConsentType.NEMURU,
  [ConditionName.TERMS_AND_CONDITIONS_ACCEPTANCE]: ConsentType.TECHNOLOGY_PROVIDER,
};

const isConsentType = (conditionName: ConditionName) => {
  return Object.keys(ConditionNameToConsentType).includes(conditionName);
};

export const useOptIns = (paymentMethodDetailEntity: LoanPaymentMethodDetailEntity) => {
  const { themeName } = useCustomTheme();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const resolveRef = useRef<((value: boolean) => void) | null>(null);
  const rejectRef = useRef<(() => void) | null>(null);

  const requiresOptIns = useMemo(() => {
    if (themeName !== CustomTheme.KTUIN) return false;
    return true;
    // const onboardingDetails = paymentMethodDetailEntity.onboardingDetails(OnboardingDetailsRole.BORROWER);
    // if (!onboardingDetails || onboardingDetails.fulfilledBy !== OnboardingFulfilledBy.MERCHANT_POS) return false;
    // return true;
  }, [themeName, paymentMethodDetailEntity]);

  const cancelWaitUntilOptInConditionChecked = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (rejectRef.current) {
      rejectRef.current();
      rejectRef.current = null;
      resolveRef.current = null;
    }
  };

  const sendOptInEmail = async () => {
    const loan = await getLoan();

    if (!loan) {
      throw new Error('Loan is required to send opt-in email');
    }

    const loanId = loan.id;
    const onboardingDetailsId = loan.onboardingDetails.find((b) => b.role === OnboardingDetailsRole.BORROWER)?.id;

    if (!onboardingDetailsId) {
      throw new Error('Onboarding details ID is required to send opt-in email');
    }

    await postSendBorrowerOptInEmail({ loanId, onboardingDetailsId });
  };

  const areOptInsChecked = async (): Promise<boolean> => {
    try {
      const optIns = await getOptIns();
      const condition = getOptInCondition(optIns);
      if (!condition || condition.checked) {
        return true;
      }
    } catch {}
    return false;
  };

  const waitUntilOptInConditionIsChecked = async () => {
    if (!requiresOptIns) return;

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    let attempts = 0;

    return new Promise<boolean>((resolve, reject) => {
      resolveRef.current = resolve;
      rejectRef.current = reject;

      intervalRef.current = setInterval(async () => {
        attempts += 1;
        if (attempts > 60) {
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          reject();
          rejectRef.current = null;
          resolveRef.current = null;
          return;
        }

        try {
          const areChecked = await areOptInsChecked();
          if (areChecked) {
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            resolve(true);
            rejectRef.current = null;
            resolveRef.current = null;
          }
        } catch {}
      }, 3000);
    });
  };

  const getOptIns = async (): Promise<OptInBorrower> => {
    const loan = await getLoan();

    if (!loan) {
      throw new Error('Loan is required to get opt ins');
    }

    const loanId = loan.id;
    const borrowerId = loan.borrowerId;

    if (!borrowerId) {
      throw new Error('Borrower ID is required to get opt ins');
    }

    return await getOptInByLoanIdAndBorrowerId({ loanId, borrowerId });
  };

  const getOptInCondition = (optIns: OptInBorrower): OptInCondition | undefined => {
    return optIns.conditionsIndicated.find((optIn) => isConsentType(optIn.name));
  };

  const getLoan = async () => {
    if (!paymentMethodDetailEntity.loan) {
      await paymentMethodDetailEntity.refreshLoan();
    }
    return paymentMethodDetailEntity.loan;
  };

  return {
    requiresOptIns,
    areOptInsChecked,
    sendOptInEmail,
    waitUntilOptInConditionIsChecked,
    cancelWaitUntilOptInConditionChecked,
  };
};
